/**
 * Lobby eines Playtests: Teilnehmer (Muster der Freunde-Liste), Einladungscode, eigene Deck-Kachel
 * mit Auswahl-Modal und darunter zentriert die Startleben (Host: Stepper, Gast: Wert). Pollt alle
 * 2 s; wird die Session aktiv, geht es sofort aufs Board.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Layers, LogOut, Play, Copy, Link as LinkIcon, Check } from 'lucide-react';
import Page from '../../../ui/Page';
import PageSplit from '../../../ui/PageSplit';
import ActionBar from '../../../ui/ActionBar';
import SectionHeader from '../../../ui/SectionHeader';
import List from '../../../ui/List';
import Badge from '../../../ui/Badge';
import Tooltip from '../../../ui/Tooltip';
import EmptyState from '../../../ui/EmptyState';
import Skeleton from '../../../ui/Skeleton';
import DeckTile from '../../../ui/DeckTile';
import Form from '../../../ui/Form';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { useToast } from '../../../ui/Toast';
import { apiClient, json } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';
import { useLobbyPolling } from '../hooks/useLobbyPolling';
import DeckPickerModal from '../components/DeckPickerModal';
import './PlaytestLobbyRoute.css';

const LIFE_MIN = 1;
const LIFE_MAX = 999;
const LIFE_DEBOUNCE_MS = 300;
const COPY_FEEDBACK_MS = 2000;

const initial = (name) => ((name || '?').trim().charAt(0) || '?').toUpperCase();

/** Kopiert `text` und meldet, ob es geklappt hat — ohne Zwischenablage-Rechte scheitert beides. */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(ta);
    return copied;
  }
}

export default function PlaytestLobbyRoute() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { session, error, failures, setSession, markWrite, refetchNow } = useLobbyPolling(sessionId);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(null);
  const [lifeDraft, setLifeDraft] = useState(null);
  const lifeTimer = useRef(null);
  const copyTimer = useRef(null);

  // Session aktiv → sofort aufs Board; offene Flächen verschwinden mit der Seite.
  useEffect(() => {
    if (session?.status === 'active') navigate(`/playtest/board/${sessionId}`, { replace: true });
  }, [session?.status, sessionId, navigate]);

  const isHost = !!session?.isHost;
  const me = session?.players?.find((p) => p.userId === session.currentUserId) ?? null;
  const canStart = !!session && session.players.length >= 1 && session.players.every((p) => p.hasDeck);
  const inviteLink = session ? `${window.location.origin}/playtest/join/${session.inviteCode}` : '';

  const handleWriteError = useCallback((err, fallback) => {
    if (err?.code === 'SESSION_NOT_WAITING') { refetchNow(); return; }
    toast.push({ tone: 'error', message: errorMessage(err, fallback) });
  }, [refetchNow, toast]);

  const handleSelectDeck = useCallback(async (deckId) => {
    markWrite();
    try {
      await apiClient.post(`/api/playtest/${sessionId}/select-deck`, { deckId });
      // Zweites markWrite(): ein Poll, der während der Anfrage startet, hätte sonst schon einen
      // akzeptierten Sequenz-Wert und könnte den alten hasDeck-Stand nach dem Refetch zurückschreiben.
      markWrite();
      await refetchNow();
    } catch (err) {
      handleWriteError(err, 'Deck konnte nicht gewählt werden.');
      if (err?.code !== 'SESSION_NOT_WAITING') throw err;
    }
  }, [sessionId, markWrite, refetchNow, handleWriteError]);

  const saveLife = useCallback(async (value) => {
    markWrite();
    try {
      const resp = await apiClient.patch(`/api/playtest/${sessionId}/settings`, { initialLife: value });
      // Zweites markWrite(): ein Poll, der während der Anfrage startet, hätte sonst schon einen
      // akzeptierten Sequenz-Wert und könnte die alte initialLife über diese Antwort schreiben.
      markWrite();
      setSession(resp.data);
    } catch (err) {
      handleWriteError(err, 'Startleben konnten nicht gespeichert werden.');
    } finally {
      setLifeDraft(null);
    }
  }, [sessionId, markWrite, setSession, handleWriteError]);

  const handleLifeChange = (value) => {
    setLifeDraft(value);
    clearTimeout(lifeTimer.current);
    lifeTimer.current = setTimeout(() => saveLife(value), LIFE_DEBOUNCE_MS);
  };
  useEffect(() => () => { clearTimeout(lifeTimer.current); clearTimeout(copyTimer.current); }, []);

  const handleCopy = async (text, kind) => {
    if (!(await copyText(text))) {
      toast.push({ tone: 'error', message: 'Kopieren nicht möglich.' });
      return;
    }
    setCopied(kind);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), COPY_FEEDBACK_MS);
  };

  const handleStart = async () => {
    setStarting(true);
    // Ein Stepper-Klick unmittelbar vor dem Start hat seinen Debounce noch offen; die Seite
    // verschwindet mit dem Board und nähme ihn mit — deshalb hier zuerst speichern.
    if (lifeDraft !== null) {
      clearTimeout(lifeTimer.current);
      await saveLife(lifeDraft);
    }
    try {
      await apiClient.post(`/api/playtest/${sessionId}/start`);
      navigate(`/playtest/board/${sessionId}`, { replace: true });
    } catch (err) {
      toast.push({ tone: 'error', message: errorMessage(err, 'Playtest konnte nicht gestartet werden.') });
      setStarting(false);
      refetchNow();
    }
  };

  // Cover und Kartenzahl der eigenen Kachel: die Lobby-Antwort trägt nur den Deck-Namen.
  const [myDecks, setMyDecks] = useState([]);
  useEffect(() => {
    if (!me?.deckId) return;
    let alive = true;
    json('/api/decks/list')
      .then((r) => { if (alive) setMyDecks(Array.isArray(r) ? r : (r?.decks || [])); })
      .catch(() => {});
    return () => { alive = false; };
  }, [me?.deckId]);
  const myDeck = myDecks.find((d) => d.id === me?.deckId);

  const handleLeave = async () => {
    try {
      if (isHost) await apiClient.delete(`/api/playtest/${sessionId}`);
      else await apiClient.post(`/api/playtest/${sessionId}/leave`);
    } catch (err) {
      toast.push({ tone: 'error', message: errorMessage(err, 'Lobby konnte nicht verlassen werden.') });
      return;
    }
    navigate('/playtest');
  };

  if (error) {
    // Vor dem Erstladen landet auch ein Netz- oder Serverfehler hier; nur 404/403 sind
    // endgültige Auskünfte über die Lobby, alles andere ist vorübergehend und darf erneut.
    const notMember = error.status === 403 && error.code === 'NOT_SESSION_MEMBER';
    const gone = notMember || error.status === 404;
    const title = notMember ? 'Du bist nicht mehr in dieser Lobby'
      : error.status === 404 ? 'Diese Lobby gibt es nicht mehr'
        : 'Verbindung unterbrochen';
    return (
      <Page>
        <EmptyState
          variant="error"
          icon={Users}
          title={title}
          onRetry={gone ? undefined : refetchNow}
          action={<button type="button" className="btn-primary" onClick={() => navigate('/playtest')}>Zum Einstieg</button>}
        />
      </Page>
    );
  }

  if (!session) {
    return (
      <Page>
        <ActionBar divider left={<ActionBar.Stat id="teilnehmer"><Skeleton width="120px" label="Lobby wird geladen" /></ActionBar.Stat>} />
        <PageSplit>
          <section><SectionHeader>Teilnehmer</SectionHeader><Skeleton height={62} /><Skeleton height={62} /></section>
          <aside><SectionHeader>Dein Deck</SectionHeader><Skeleton height={210} radius="lg" /></aside>
        </PageSplit>
      </Page>
    );
  }

  // Die Weiterleitung aufs Board liegt in einem Effekt und greift erst nach diesem Durchlauf —
  // ohne diese Bremse blitzt die Lobby einmal mit Board-Daten auf.
  if (session.status === 'active') return null;

  const lifeShown = lifeDraft ?? session.initialLife;

  return (
    <Page>
      <ActionBar
        divider
        left={
          <>
            <ActionBar.Stat id="teilnehmer" value={`${session.players.length} / ${session.maxPlayers}`} label="Teilnehmer" />
            <ActionBar.Stat id="leben" value={lifeShown} label="Startleben" />
          </>
        }
        right={
          <>
            <ActionBar.Action icon={LogOut} label={isHost ? 'Lobby beenden' : 'Lobby verlassen'} onClick={() => (isHost ? setConfirmEnd(true) : handleLeave())} />
            {isHost && (
              <ActionBar.Action icon={Play} label={starting ? 'Startet…' : 'Playtest starten'} variant="primary" disabled={!canStart || starting} onClick={handleStart} />
            )}
          </>
        }
      />

      {failures >= 3 && <p className="lobby-hint" role="status">Verbindung unterbrochen – versuche es weiter …</p>}
      {isHost && !canStart && <p className="lobby-hint">Alle Teilnehmer müssen ein Deck wählen, bevor du starten kannst.</p>}

      <section className="lobby-invite">
        <div className="lobby-invite__text">
          <span className="lobby-invite__label">Einladungscode</span>
          <code className="lobby-invite__code">{session.inviteCode}</code>
          <Tooltip label={copied === 'code' ? 'Kopiert' : 'Einladungscode kopieren'}>
            <button
              type="button"
              className="btn-icon lobby-invite__copy"
              onClick={() => handleCopy(session.inviteCode, 'code')}
            >
              {copied === 'code' ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
            </button>
          </Tooltip>
        </div>
        <div className="lobby-invite__actions">
          <button type="button" className="btn-secondary" onClick={() => handleCopy(inviteLink, 'link')}>
            {copied === 'link' ? <><Check size={14} aria-hidden /> Kopiert</> : <><LinkIcon size={14} aria-hidden /> Einladungslink kopieren</>}
          </button>
        </div>
      </section>

      <PageSplit>
        <section>
          <SectionHeader>Teilnehmer</SectionHeader>
          <List>
            {session.players.map((p) => {
              const meta = [p.userId === session.hostId && 'Host', p.userId === session.currentUserId && 'du'].filter(Boolean).join(' · ');
              return (
                <List.Item key={p.userId}>
                  <List.Avatar>{initial(p.displayName)}</List.Avatar>
                  <List.Main>
                    <List.Name>{p.displayName || 'Teilnehmer'}</List.Name>
                    {meta && <List.Meta>{meta}</List.Meta>}
                  </List.Main>
                  <List.Actions>
                    {p.hasDeck
                      ? <Badge tone="accent"><Check size={14} aria-hidden /> {p.deckName}</Badge>
                      : <Badge tone="pending">Wählt Deck…</Badge>}
                  </List.Actions>
                </List.Item>
              );
            })}
          </List>
        </section>

        <aside>
          <SectionHeader>Dein Deck</SectionHeader>
          {me?.hasDeck ? (
            <DeckTile name={me.deckName} image={myDeck?.commanderImageUrl || undefined} artist={myDeck?.commanderArtist || undefined} count={myDeck?.cardCount} onClick={() => setPickerOpen(true)} clickLabel="Deck wechseln" />
          ) : (
            <EmptyState
              size="compact"
              icon={Layers}
              title="Noch kein Deck gewählt"
              action={<button type="button" className="btn-primary" onClick={() => setPickerOpen(true)}>Deck wählen</button>}
            />
          )}

          <div className="lobby-life">
            <span className="lobby-life__label">Startleben</span>
            {isHost
              ? <Form.Stepper value={lifeShown} min={LIFE_MIN} max={LIFE_MAX} onChange={handleLifeChange} ariaLabel="Startleben" />
              : <p className="lobby-life__value">{session.initialLife}</p>}
          </div>
        </aside>
      </PageSplit>

      <DeckPickerModal open={pickerOpen} onOpenChange={setPickerOpen} currentDeckId={me?.deckId ?? null} onConfirm={handleSelectDeck} />

      <ConfirmDialog
        open={confirmEnd}
        onOpenChange={setConfirmEnd}
        title="Lobby für alle beenden?"
        description="Alle Teilnehmer verlieren diese Lobby; der Einladungscode wird ungültig."
        confirmLabel="Beenden"
        tone="danger"
        onConfirm={handleLeave}
      />
    </Page>
  );
}
